import { type CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";

import { env as configEnv } from "@/config";

export interface FormattedPhoneNumber {
    number: string;
    countryCode: string;
}

export function parsePhoneNumber(
    value: string,
    country: CountryCode = "US",
): FormattedPhoneNumber & { possible: boolean } {
    const parsed = parsePhoneNumberFromString(value, { defaultCountry: country });
    const countryCode = parsed?.countryCallingCode ?? "";
    const number =
        countryCode === "1"
            ? parsed?.formatNational()
            : parsed?.formatInternational().substring(`+${countryCode} `.length);

    return {
        countryCode,
        number: number ?? "",
        possible: parsed?.isPossible() ?? false,
    };
}

export function formatPhoneNumber(
    value: string,
    country: CountryCode = "US",
    skipValidation: boolean = false,
): FormattedPhoneNumber {
    if (!value) {
        throw new Error(`Invalid phone number format, ${value}`);
    }

    const parsed = parsePhoneNumberFromString(value, { defaultCountry: country });
    const countryCode = parsed?.countryCallingCode ?? "";

    if (!skipValidation) {
        const isValid = configEnv.isProdMode ? parsed?.isValid() : parsed?.isPossible();

        if (!isValid) {
            throw new Error(`Invalid phone number, ${value}`);
        }
    }

    if (countryCode === "1") {
        return {
            number: parsed?.formatNational() ?? "",
            countryCode,
        };
    }

    const formattedNumber = parsed?.formatInternational().substring(`+${countryCode} `.length) ?? "";

    return {
        number: formattedNumber,
        countryCode,
    };
}

export function removeCountryCode(number: string, prefix = "+1"): string {
    if (!number.startsWith(prefix)) {
        return number;
    }

    return number.slice(prefix.length);
}

export function formatSip(mdn: string) {
    const index = mdn.indexOf(":");
    if (index === -1) {
        return mdn;
    }
    const noSip = mdn.substring(index + 1);
    return noSip.substring(0, 12);
}
